import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { getUrl } from 'aws-amplify/storage'
import RequestJoinModal from './RequestJoinModal'
import ConvertToOrganizationModal from './ConvertToOrganizationModal'
import LoadingSpinner from '../../common/LoadingSpinner'
import SimpleButton from '../../common/SimpleButton'
import ConfirmationModal from '../../common/ConfirmationModal'
import ActionsMenu from '../../common/ActionsMenu'
import GenericTable from '../../common/GenericTable'
import Dropdown from '../../common/Dropdown'
import ExpandableText from '../../common/ExpandableText'
import { toast } from '../../common/Toast'
import EmptyPageResults from '../../common/EmptyPageResults'
import GoogleAddressInput from '../../common/GoogleAddressInput'
import CheckboxInput from '../../common/CheckboxInput'
import config from '../../../utils/config'
import { sendMail } from '../../../utils/requests/mqttPublishAPI'
import { hasCorporateAdminModulesPermission, organizationAdminModulesPermission } from '../../../utils/permissionValidation'
import { postCompanyLogo } from '../../../utils/requests/companyAPI'
import { getCurrentUserPermissions, validateCompanyName } from '../../../utils/requests/usersAPI'
import { useWindowSize } from '../../../libs/hooks'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import {
  loadUserCompany,
  loadCompanyLogo,
  removeCompanyLogo,
  clearUserCompanyMessages,
  leaveCompany,
  loadCompanyRequests,
  acceptCompanyRequest,
  rejectCompanyRequest,
  cancelCompanyRequest,
  resendCompanyRequest,
  updateBranchInformation,
} from '../../../actions/userCompany'
import { COLTENE_BRANCH_UUID } from '../../../actions/root'
import { handleUpdateAuthedUser } from '../../../actions/authedUser'
import { handleSetCurrentUserPermissions } from '../../../actions/userPermissions'
import {
  CaretDownIcon,
  CompanyBuilding,
  DisabledIcon,
  MenuKebabVerticalBackgroundIcon,
  MenuKebabVerticalIcon,
  PendingIcon,
  PhoneIcon,
  PhotoIcon,
} from '../../../icons'
import { capitalizeFirstLetter, formatPhone } from '../../../utils/functions'
import { convertTimeWithTAndZ } from '../../../utils/filters/date'
import { drAuthRules } from '../../../utils/drConfig'
import './my-organization.scss'

const orderingFields = [
  {
    field: 'lastModifyAt',
    order: 'asc',
    translationKey: 'date_asc',
  },
  {
    field: 'lastModifyAt',
    order: 'desc',
    translationKey: 'date_desc',
  },
  {
    field: 'companyName',
    order: 'asc',
    translationKey: 'organization_asc',
  },
  {
    field: 'companyName',
    order: 'desc',
    translationKey: 'organization_desc',
  },
  {
    field: 'status',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'status',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const MyOrganization = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { isCompanyAccount, companyId, branchId, branchCountry, companyLogo } = props
  const [colteneBranchId, setColteneBranchId, removeColteneBranchId] = useLocalStorage(COLTENE_BRANCH_UUID)
  const [companyLogoUrl, setCompanyLogoUrl] = useState('')
  const [branchName, setBranchName] = useState('')
  const [branchAddress, setBranchAddress] = useState('')
  const [branchCity, setBranchCity] = useState('')
  const [branchState, setBranchState] = useState('')
  const [branchZip, setBranchZip] = useState('')
  const [branchPhone, setBranchPhone] = useState('')
  const [haveError, setHaveError] = useState({})
  const [showRequestJoinModal, setShowRequestJoinModal] = useState(false)
  const [showConvertToOrganizationModal, setShowConvertToOrganizationModal] = useState(false)
  const [showLeaveCompanyConfirmationModal, setShowLeaveCompanyConfirmationModal] = useState(false)
  const [requests, setRequests] = useState([])
  const [hasRequestedAlready, setHasRequestedAlready] = useState(false)
  const [orderBy, setOrderBy] = useState(null)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [expandedRequest, setExpandedRequest] = useState(0)
  const [showSpinner, setShowSpinner] = useState(null)
  const [hasCompanyInformationChanges, setHasCompanyInformationChanges] = useState(false)
  const [keepDentistDevices, setKeepDentistDevices] = useState(false)
  const isCompanyMember = companyId && props.companyStatus === 'approved'
  const isBranchAdmin = props.branchAdmin.toLowerCase() === props.currentUserEmail.toLowerCase() ? true : false
  const phonePattern = new RegExp(/^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g)

  useEffect(() => {
    props.actions.loadUserCompany(colteneBranchId)
  }, [props.actions])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearUserCompanyMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearUserCompanyMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    setBranchName(props.branchName)
    setBranchAddress(props.branchAddress)
    setBranchCity(props.branchCity)
    setBranchState(props.branchState)
    setBranchZip(props.branchZip)
    setBranchPhone(formatPhone(props.branchPhone || ''))
  }, [props.branchName, props.branchAddress, props.branchCity, props.branchState, props.branchZip, props.branchPhone])

  useEffect(() => {
    if (companyId && branchId && !companyLogo) {
      props.actions.loadCompanyLogo(companyId, branchId)
    }
  }, [branchId, companyId])

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const { url } = await getUrl({
          key: companyLogo,
          options: {
            accessLevel: 'public',
          },
        })
        setCompanyLogoUrl(url)
      } catch (error) {
        console.error('Failed to load company logo', error)
      }
    }

    if (companyLogo) {
      fetchLogo()

      return
    }

    setCompanyLogoUrl('')
  }, [companyLogo])

  useEffect(() => {
    const filteredRequests = props.companyRequests.filter((request) => {
      if (!isCompanyMember) {
        return true
      }

      return request.isCorporationRequest === true
    })
    const mappedRequests =
      filteredRequests.map((request) => {
        const showCancel = request.requestType === 'request' && (request.status === 'requested' || request.status === 'expired')
        const showAcceptReject = request.requestType === 'invite' && request.status === 'invited'
        const showResend = request.requestType === 'request' && request.status === 'expired'

        const enableButtons =
          (!isCompanyMember || (isCompanyMember && request.isCorporationRequest === true && props.companyIsCorporate === false)) &&
          drAuthRules.isCompanyAccountUpdateAllowed

        return {
          ...request,
          date: convertTimeWithTAndZ(request.lastModifyAt),
          company: request.companyName,
          email: request.requestType === 'request' ? request.email : request.createdBy,
          requestType: request.requestType,
          status: request.status,
          actions: [
            ...(showAcceptReject
              ? [
                  {
                    label: 'accept',
                    isDisabled: !enableButtons,
                    onActionClick: (e) => {
                      e.stopPropagation()
                      handleAcceptRequest(request.id, request.role?.name)
                    },
                  },
                  {
                    label: 'reject',
                    isDisabled: !enableButtons,
                    onActionClick: (e) => {
                      e.stopPropagation()
                      handleRejectRequest(request.id)
                    },
                  },
                ]
              : []),
            ...(showResend
              ? [
                  {
                    label: 'resend',
                    isDisabled: !drAuthRules.isCompanyAccountUpdateAllowed,
                    onActionClick: (e) => {
                      e.stopPropagation()
                      handleResendRequest(request.id)
                    },
                  },
                ]
              : []),
            ...(showCancel
              ? [
                  {
                    label: request.status === 'requested' ? 'cancel' : 'remove',
                    isDisabled: !drAuthRules.isCompanyAccountUpdateAllowed,
                    onActionClick: (e) => {
                      e.stopPropagation()
                      handleCancelRequest(request.id)
                    },
                  },
                ]
              : []),
          ],
        }
      }) || []

    setRequests(mappedRequests)
    setHasRequestedAlready(mappedRequests.some((request) => request.requestType === 'request'))
  }, [props.companyRequests])

  const fetchInvites = useCallback(async () => {
    let queryParams = {}

    if (orderBy) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    props.actions.loadCompanyRequests(queryParams)
  }, [orderBy])

  useEffect(() => {
    if (!hasCorporateAdminModulesPermission(props.userPermissions)) {
      fetchInvites()
    }
  }, [orderBy, props.userPermissions])

  useEffect(() => {
    const hasChanges =
      branchName !== props.branchName ||
      branchAddress !== props.branchAddress ||
      branchCity !== props.branchCity ||
      branchState !== props.branchState ||
      branchZip !== props.branchZip ||
      branchPhone !== formatPhone(props.branchPhone || '')

    setHasCompanyInformationChanges(hasChanges)
  }, [
    branchName,
    branchAddress,
    branchCity,
    branchState,
    branchZip,
    branchPhone,
    props.branchName,
    props.branchAddress,
    props.branchCity,
    props.branchState,
    props.branchZip,
    props.branchPhone,
  ])

  const toggleShowActionsMenu = (identifier) => {
    setShowActionsMenu(showActionsMenu === identifier ? null : identifier)
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleAcceptRequest = (requestId, newRole) => {
    props.actions.acceptCompanyRequest(requestId).then(async () => {
      props.actions.loadUserCompany().then((result) => {
        setColteneBranchId(result?.value?.branch[0]?.branchUuid)
      })

      const currentUserRole = props.authedUser?.userGroup.split(/[_-]/)[0]
      const newRoleFormatted = newRole?.toLowerCase()?.replace(/ /g, '_')

      if (!newRoleFormatted || currentUserRole === newRoleFormatted) {
        props.actions.handleUpdateAuthedUser({
          companyAccountType: 'organization_member',
        })

        return
      }

      const permissions = await getCurrentUserPermissions()
      props.actions.handleSetCurrentUserPermissions(permissions)

      props.actions.handleUpdateAuthedUser({
        userGroup: newRoleFormatted,
        companyAccountType: 'organization_member',
      })
    })
  }

  const handleRejectRequest = (requestId) => {
    props.actions.rejectCompanyRequest(requestId)
  }

  const handleCancelRequest = (requestId) => {
    props.actions.cancelCompanyRequest(requestId)
  }

  const handleResendRequest = (requestId) => {
    props.actions.resendCompanyRequest(requestId)
  }

  const handleLeaveCompany = () => {
    const bodyParams = {
      ...(false && { keepDeviceWithMember: keepDentistDevices }),
    }

    props.actions.leaveCompany(bodyParams).then(() => {
      removeColteneBranchId()

      props.actions.handleUpdateAuthedUser({
        companyAccountType: 'individual',
      })
    })
  }

  const handleRemoveAvatar = () => {
    props.actions.removeCompanyLogo(companyId, branchId)
  }

  const handleAddAvatar = async (event) => {
    try {
      const file = event?.target?.files[0]
      const reader = new FileReader()

      if (file?.size > 1 * 1024 * 1024) {
        toast.error(t('error.file_size_one_mb'))
        return
      }

      reader.onloadend = async () => {
        const base64String = reader.result?.split(',')[1]
        const bodyParams = {
          fileName: file?.name,
          fileContent: base64String,
        }

        setShowSpinner(true)
        await postCompanyLogo(companyId, branchId, bodyParams)
        setShowSpinner(false)

        props.actions.loadCompanyLogo(companyId, branchId)
      }

      reader.readAsDataURL(file)
    } catch (error) {
      toast.error(t('error.something_wrong'))
    }
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleCancelCompanyInformationChanges = () => {
    setBranchName(props.branchName)
    setBranchAddress(props.branchAddress)
    setBranchCity(props.branchCity)
    setBranchState(props.branchState)
    setBranchZip(props.branchZip)
    setBranchPhone(formatPhone(props.branchPhone || ''))
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!branchName.trim()) {
      errors = { ...errors, name: true }
      isValid = false
    }

    if (!branchPhone.trim() || !phonePattern.test(branchPhone)) {
      errors = { ...errors, phone: true }
      isValid = false
    }

    if (!branchAddress.trim()) {
      errors = { ...errors, address: true }
      isValid = false
    }

    if (!branchCity.trim()) {
      errors = { ...errors, city: true }
      isValid = false
    }

    if (!branchState.trim()) {
      errors = { ...errors, stateProvince: true }
      isValid = false
    }

    if (!branchZip.trim()) {
      errors = { ...errors, zipCode: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleUpdateBranchInformation = async () => {
    if (!isValidInput()) {
      return
    }

    const bodyParams = {
      branchName: branchName.trim(),
      email: props.branchAdmin,
      country: branchCountry,
      officeAddressOne: branchAddress.trim(),
      city: branchCity.trim(),
      stateProvinceRegion: branchState.trim(),
      zipPostalCode: branchZip.trim(),
      telephone: branchPhone.trim(),
    }

    props.actions.updateBranchInformation(bodyParams)
  }

  const handleRequestHelp = async () => {
    const emailTemplateVars = {
      email: `${props.authedUser?.email}`,
      company: `${props.authedUser?.company}`,
      firstname: `${props.authedUser?.firstname}`,
      lastname: `${props.authedUser?.lastname}`,
      phone: `${props.authedUser?.phone}`,
      country: `${props.authedUser?.country}`,
      language: `${props.authedUser?.language}`,
    }

    const templateName = `organization_registration_get_help_en_CA`
    const email = config.organizationReviewEmailTemplate.email

    try {
      await sendMail(email, templateName, emailTemplateVars)

      toast.success(t('register.email_sent'))
    } catch (error) {
      toast.error(t('error.something_wrong'))
    }
  }

  const handleCheckCompanyExists = async () => {
    if (branchName.trim().length > 0 && branchName.trim() !== props.branchName) {
      setShowSpinner(true)

      const queryParams = { name: branchName.trim() }
      const companyValidationResponse = await validateCompanyName(queryParams)

      setShowSpinner(false)

      if (companyValidationResponse.isCompanyExists) {
        toast.error(t('error.company_name_exists_support'))

        setHaveError((prevError) => ({
          ...prevError,
          name: false,
          companyExists: true,
        }))
      }

      return
    }

    setHaveError((prevError) => ({
      ...prevError,
      name: false,
      companyExists: false,
    }))
  }

  return (
    <div className="my-company-wrapper">
      {!isCompanyMember && (
        <div className="section-wrapper locked-section">
          {!showSpinner && (
            <>
              <div className="locked-section-title">
                {!hasRequestedAlready && !isCompanyAccount ? t('account_context.get_access') : t('account_context.pending_approval')}
              </div>
              <div className="locked-section-subtitle">
                {!hasRequestedAlready && !isCompanyAccount && t('account_context.get_access_disclaimer')}
                {hasRequestedAlready && t('account_context.get_access_requeste_disclaimer')}
                {isCompanyAccount && (
                  <div>
                    {t('account_context.get_access_converted_disclaimer')}
                    <span className="link-action" onClick={() => handleRequestHelp()}>
                      {capitalizeFirstLetter(t('nav.request_help'))}
                    </span>
                  </div>
                )}
              </div>
              {!hasRequestedAlready && !isCompanyAccount && (
                <div className="locked-section-buttons-wrapper">
                  <SimpleButton className="action-button" onClick={() => setShowRequestJoinModal(true)} disabled={!drAuthRules.isCompanyAccountUpdateAllowed}>
                    {t('account_context.request_to_join')}
                  </SimpleButton>
                  <SimpleButton
                    className="action-button"
                    onClick={() => setShowConvertToOrganizationModal(true)}
                    disabled={!drAuthRules.isCompanyAccountUpdateAllowed}
                  >
                    {t('account_context.convert_to_organization')}
                  </SimpleButton>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {isCompanyMember && (
        <div className="section-wrapper">
          <div className="wrapper-header">
            <p className="wrapper-title">{t('account_context.organization_details')}</p>
            {!isCompanyAccount && !isBranchAdmin && (
              <div className="buttons-wrapper">
                <SimpleButton
                  className="action-button"
                  onClick={() => setShowLeaveCompanyConfirmationModal(true)}
                  disabled={!drAuthRules.isCompanyAccountUpdateAllowed}
                >
                  {t('account_context.leave_organization')}
                </SimpleButton>
              </div>
            )}
          </div>
          <div className="section">
            <div className="section-title">
              <p>{t('account_context.organization_logo')}</p>
              <p className="disclaimer">{t('account_context.photo_discalimer')}</p>
            </div>
            <div className="section-content avatar">
              <div className="avatar-wrapper">
                <label className="file-input-wrapper" htmlFor="file-input">
                  <div className="avatar-img">
                    {companyLogoUrl ? <img className="image" src={companyLogoUrl} alt="avatar" /> : <div className="avatar-initials"></div>}
                  </div>
                  {isBranchAdmin && <PhotoIcon />}
                </label>
                {isBranchAdmin && <input className="file-input" id="file-input" onChange={handleAddAvatar} type="file" accept="image/*" />}
              </div>
              {isBranchAdmin && (
                <SimpleButton
                  className="action-button remove-image-button"
                  onClick={() => handleRemoveAvatar()}
                  disabled={!companyLogoUrl || showSpinner || !drAuthRules.isCompanyAccountUpdateAllowed}
                  requiredPermission={organizationAdminModulesPermission}
                >
                  {t('remove')}
                </SimpleButton>
              )}
            </div>
          </div>
          <div className="separator" />
          <div className="section">
            <div className="section-title">{t('account_context.organization_information')}</div>
            <div className="section-content">
              <div className={'input-item-wrapper' + (haveError['name'] || haveError['companyExists'] ? ' has-error' : '')}>
                <div className="input-item-icon">
                  <CompanyBuilding />
                </div>
                <input
                  type="text"
                  name="company"
                  className="input-item"
                  value={branchName}
                  placeholder={`${t('company')}*`}
                  onChange={(e) => setBranchName(e.target.value)}
                  onBlur={handleCheckCompanyExists}
                  readOnly={!isBranchAdmin}
                />
                {haveError['name'] && <p className="error-message">{t('account_context.organization_name_required')}</p>}
                {haveError['companyExists'] && <p className="error-message">{t('error.company_name_exists')}</p>}
              </div>
            </div>
          </div>
          <div className="section">
            <div className="section-title empty" />
            <div className="section-content">
              <GoogleAddressInput
                country={branchCountry}
                address={branchAddress}
                handleSetAddress={(e) => setBranchAddress(e)}
                city={branchCity}
                handleSetCity={(e) => setBranchCity(e)}
                stateProvince={branchState}
                handleSetStateProvince={(e) => setBranchState(e)}
                zipCode={branchZip}
                handleSetZipCode={(e) => setBranchZip(e)}
                withIcons
                withoutLabels
                readOnly={!isBranchAdmin}
                errors={haveError}
                withErrorLabels
                withRequiredPlaceholder
              />
            </div>
          </div>
          <div className="section">
            <div className="section-title empty" />
            <div className="section-content without-margin-top">
              <div className={'input-item-wrapper' + (haveError['phone'] ? ' has-error' : '')}>
                <div className="input-item-icon">
                  <PhoneIcon />
                </div>
                <input
                  type="text"
                  name="company"
                  className="input-item"
                  value={formatPhone(branchPhone)}
                  placeholder={`${t('phone')}*`}
                  onChange={(e) => setBranchPhone(e.target.value)}
                  readOnly={!isBranchAdmin}
                />
                {haveError['phone'] && <p className="error-message">{t('error.phone')}</p>}
              </div>
            </div>
          </div>
          {isBranchAdmin && (
            <div className="section full-width">
              <div className="section-title empty" />
              <div className="section-content buttons">
                <SimpleButton className="cancel-button" disabled={!hasCompanyInformationChanges} onClick={handleCancelCompanyInformationChanges}>
                  {t('cancel')}
                </SimpleButton>
                <SimpleButton
                  className="submit-button"
                  disabled={!hasCompanyInformationChanges || showSpinner || !drAuthRules.isCompanyAccountUpdateAllowed}
                  onClick={handleUpdateBranchInformation}
                  requiredPermission={organizationAdminModulesPermission}
                >
                  {t('save')}
                </SimpleButton>
              </div>
            </div>
          )}
        </div>
      )}

      {!showSpinner && !hasCorporateAdminModulesPermission(props.userPermissions) && (
        <div className="section-wrapper">
          <div className="wrapper-header">
            <p className="wrapper-title">{t('organization_context.my_requests')}</p>
          </div>
          {
            <div>
              {!isMobile && requests.length > 0 && (
                <>
                  <GenericTable
                    data={requests}
                    headers={[
                      {
                        title: t('date'),
                        orderKey: 'lastModifyAt',
                      },
                      {
                        title: t('organization'),
                        orderKey: 'companyName',
                      },
                      {
                        title: t('email'),
                      },
                      {
                        title: t('request_type'),
                      },
                      {
                        title: t('status'),
                        orderKey: 'status',
                      },
                      {
                        title: '',
                      },
                    ]}
                    keys={['date', 'company', 'email', 'requestType', 'status', 'action']}
                    keyRenderer={{
                      createdBy: (item) => {
                        return (
                          <div className="email-wrapper">
                            <ExpandableText text={item.email} />
                          </div>
                        )
                      },
                      requestType: (item) => {
                        return <>{t([`dealer-portal.${item.requestType}`, item.requestType])}</>
                      },
                      status: (item) => {
                        return (
                          <div className="status-wrapper">
                            <div className={item.status === 'expired' ? 'status-inactive' : 'status-pending'}>
                              {item.status === 'expired' ? <DisabledIcon /> : <PendingIcon />}
                              <p>{t(item.status)}</p>
                            </div>
                          </div>
                        )
                      },
                      action: (item) => {
                        return (
                          <div
                            className="table-actions-wrapper"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleShowActionsMenu(item.id)
                            }}
                          >
                            {showActionsMenu === item.id ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                            {showActionsMenu === item.id && (
                              <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                            )}
                          </div>
                        )
                      },
                    }}
                    activeSort={orderBy}
                    onSort={(orderingField) => handleSort(orderingField)}
                    isLoading={showSpinner}
                  />
                </>
              )}
              {isMobile && requests.length > 0 && (
                <>
                  <div className="order-by-mobile-wrapper">
                    <Dropdown
                      className="dropdown-input"
                      placeholder={t('select_ordering')}
                      options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
                      initialSelectedKey={orderBy?.translationKey}
                      onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
                    />
                  </div>
                  <div className="cards-container">
                    {requests.map((item, rowIndex) => {
                      return (
                        <div
                          key={rowIndex}
                          className={'card' + (expandedRequest === rowIndex ? ' with-padding-bottom' : '')}
                          onClick={() => setExpandedRequest(rowIndex)}
                        >
                          <div className={'card-item'}>
                            <div className="card-item-title">{t('organization')}</div>
                            <div className={'card-item-body'}>
                              <div>{item.company}</div>
                              <div
                                className="card-actions"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setShowActionsMenu(rowIndex)
                                }}
                              >
                                {showActionsMenu === rowIndex ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                                {showActionsMenu === rowIndex && (
                                  <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                                )}
                              </div>
                            </div>
                          </div>
                          {expandedRequest === rowIndex && (
                            <>
                              <div className="card-item">
                                <div className="card-item-title">{t('date')}</div>
                                <div className="card-item-body">{item.date}</div>
                              </div>
                              <div className="card-item">
                                <div className="card-item-title">{t('email')}</div>
                                <div className="card-item-body">{item.email}</div>
                              </div>
                              <div className="card-item">
                                <div className="card-item-title">{t('request_type')}</div>
                                <div className="card-item-body">{t([`dealer-portal.${item.requestType}`, item.requestType])}</div>
                              </div>
                              <div className="card-item">
                                <div className="card-item-title">{t('status')}</div>
                                <div className="card-item-body">
                                  <div className={item.status === 'expired' ? 'status-inactive' : 'status-pending'}>
                                    <p>{t(item.status)}</p>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          {expandedRequest !== rowIndex && (
                            <div className="expander-wrapper">
                              <CaretDownIcon />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          }
          {!showSpinner && requests.length === 0 && (
            <EmptyPageResults
              className="empty-page-results"
              title={t('organization_context.no-requests-found')}
              subtitle={t('organization_context.no-requests-found-extra')}
            />
          )}
        </div>
      )}
      {showRequestJoinModal && <RequestJoinModal handleClose={() => setShowRequestJoinModal(false)} fetchInvites={fetchInvites} />}
      {showConvertToOrganizationModal && <ConvertToOrganizationModal closeModal={() => setShowConvertToOrganizationModal(false)} />}
      {showLeaveCompanyConfirmationModal && (
        <ConfirmationModal
          className="leave-company-confirmation"
          onToggle={() => setShowLeaveCompanyConfirmationModal(false)}
          message={t('account_context.leave_organization_confirmation')}
          acceptButtonText={t('account_context.leave')}
          onCancel={() => setShowLeaveCompanyConfirmationModal(false)}
          onAccept={() => {
            handleLeaveCompany()
            setShowLeaveCompanyConfirmationModal(false)
          }}
          isWarning
        >
          {/*A.S: To be hidden until requirement is established */}
          {false && (
            <div className="leave-confirmation-wrapper">
              <div className="disclaimer">
                <p>{t('account_context.keep_devices_disclaimer')}</p>
              </div>
              <CheckboxInput
                checked={keepDentistDevices}
                text={t('account_context.keep_devices')}
                onChange={() => setKeepDentistDevices(!keepDentistDevices)}
              />
            </div>
          )}
        </ConfirmationModal>
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ authedUser, userCompany, userPermissions }) {
  return {
    authedUser,
    companyId: userCompany?.companyId,
    companyStatus: userCompany?.companyStatus || '',
    companyIsCorporate: userCompany?.companyIsCorporate,
    branchId: userCompany?.branchId,
    branchName: userCompany?.branchName || '',
    branchAddress: userCompany?.branchAddress || '',
    branchCity: userCompany?.branchCity || '',
    branchState: userCompany?.branchState || '',
    branchZip: userCompany?.branchZip || '',
    branchCountry: userCompany?.branchCountry || '',
    branchAdmin: userCompany?.branchAdmin || '',
    branchPhone: userCompany?.branchPhone || '',
    companyLogo: userCompany?.companyLogo,
    errorMessage: userCompany?.errorMessage,
    successMessage: userCompany?.successMessage,
    isLoading: userCompany?.isLoading,
    isCompanyAccount: authedUser?.companyAccountType === 'organization_admin' || authedUser?.companyAccountType === 'corporate_admin' || false,
    currentUserEmail: authedUser?.email || '',
    currentUserGroup: authedUser?.userGroup || '',
    companyRequests: userCompany?.companyRequests || [],
    userPermissions,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadUserCompany,
        loadCompanyLogo,
        loadCompanyRequests,
        removeCompanyLogo,
        leaveCompany,
        acceptCompanyRequest,
        rejectCompanyRequest,
        cancelCompanyRequest,
        resendCompanyRequest,
        updateBranchInformation,
        clearUserCompanyMessages,
        handleUpdateAuthedUser,
        handleSetCurrentUserPermissions,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(MyOrganization)

